import * as React from "react";
import { useRouteMatch } from "react-router-dom";
import { AccessReportParametersModel } from "@app/access-reports/wizard";
import { NotFound } from "@app/shared/ErrorPages";
import { Message, MessageType } from "@app/notifications";
import { FC, JSX, useContext, useEffect, useRef, useState } from "react";
import { NotificationContext } from "@app/notifications/NotificationContext";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons"; 
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { UserContext } from "@app/user/UserContext";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { preventDefaultMouseEvent } from "@app/shared/utility/preventDefaultMouseEvent";
import AccessReportsService from "@app/access-reports/accessReportsService";
import { AccessReportSummary } from "@app/access-reports/dashboard/AccessReportSummary";
import { AccessReportRunStatusChangeNotificationMessagePayload } from "@app/access-reports/dashboard/shared/accessReportRunStatusChangeNotificationMessagePayload";
import { AccessReportStatusEnum } from "@app/access-reports/dashboard/shared/accessReportStatusEnum";
import { AccessReport } from "@app/access-reports/shared/accessReport";
import { ArchiveAccessReportsBulkModel } from "@app/access-reports/shared/archiveAccessReportsBulkModel";
import { handleFileLinkClick } from "@app/access-reports/shared/utility";

export const AccessReportOverview: FC = () => {
  const accessReportsService = useRef(new AccessReportsService()).current;
  const routeMatch = useRouteMatch<{ accessReportId: string }>();
  const user = useContext(UserContext);
  const notificationContext = useContext(NotificationContext);


  const [accessReport, setAccessReport] = useState<AccessReport>({} as AccessReport);
  const [loading, setLoading] = useState(true);
  const [statusBanner, setStatusBanner] = useState<React.ReactNode>(null);

  useEffect(() => {
    getAccessReport();
    notificationContext.subscribe(handleNotifications);

    return () => {
      notificationContext.unsubscribe(handleNotifications);
    }
  }, []);

  useEffect(() => {
    getStatusBanner();
  }, [accessReport]);

  const getAccessReport = async (): Promise<void> => {
    try {
      const newAccessReport: AccessReport = await accessReportsService.getAccessReport(Number(routeMatch.params.accessReportId));
      newAccessReport.reportTitle = !!newAccessReport.reportTitle ? newAccessReport.reportTitle.trim() : `Access Report ${newAccessReport.accessReportId}`

      setAccessReport(newAccessReport);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }

  const getStatusBanner = (): void => {
    let result: JSX.Element = null;

    switch (accessReport.status) {
      case AccessReportStatusEnum.Created:
        result = (
          <React.Fragment>
            <QESAlert classNames="mb-3" type="info" persist={true}>
              <strong>Please wait!</strong> Your project has been created and has been queued for processing.
            </QESAlert>
            <RoleRestricted allowedRoles={[Roles.ACCESS_REPORTS_WIZARD_ADMIN]}>
              <QESAlert classNames="mb-3" type="danger" persist={true}>
                <strong>Warning!</strong> You can fail your project manually if you believe it will not finish.
                <QESButton
                  qesTestId="access-reports-dashboard__overview--fail-job"
                  className="ms-3"
                  onClick={failProject}
                >
                  Fail Project
                </QESButton>
              </QESAlert>
            </RoleRestricted>
          </React.Fragment>
        );
        break;
      case AccessReportStatusEnum.Canceled:
      case AccessReportStatusEnum.Failed:
        if (!accessReport.dateArchived) {
          result = (
            <React.Fragment>
              <QESAlert classNames="mb-3" type="warning" persist={true}>
                <strong>Warning!</strong> Your latest project run failed.
                <QESButton
                  qesTestId="access-reports-dashboard__overview--run-again"
                  className="ms-3"
                  onClick={runProject}
                >
                  <QesIcon iconName={QesIcons.RocketV2} description="Run" className="mb2" /> Run Again
                </QESButton>
              </QESAlert>
              <RoleRestricted allowedRoles={[Roles.ACCESS_REPORTS_WIZARD_ADMIN]}>
                <QESAlert classNames="mb-3" type="info" persist={true}>
                  Your project can be archived.

                  <QESButton
                    qesTestId="access-reports-dashboard__overview--archive"
                    className="ms-3"
                    onClick={archiveProject}
                  >
                    <QesIcon iconName={QesIcons.Archive} description="Archive" className="mb2" /> Archive Project
                  </QESButton>
                </QESAlert>
              </RoleRestricted>
            </React.Fragment>
          );
        }
        else {
          result = (
            <RoleRestricted allowedRoles={[Roles.ACCESS_REPORTS_WIZARD_ADMIN]}>
              <QESAlert classNames="mb-3" type="info" persist={true}>
                Your project can be unarchived.

                <QESButton
                  qesTestId="access-reports-dashboard__overview--unarchive"
                  className="ms-3"
                  onClick={unarchiveProject}
                >
                  <QesIcon iconName={QesIcons.Archive} description="Unarchive" className="mb2" /> Unarchive Project
                </QESButton>
              </QESAlert>
            </RoleRestricted>
          );
        }

        break;
      case AccessReportStatusEnum.Running:
        result = (
          <React.Fragment>
            <QESAlert classNames="mb-3" type="info" persist={true}>
              Your project is currently running.
            </QESAlert>
            <RoleRestricted allowedRoles={[Roles.ACCESS_REPORTS_WIZARD_ADMIN]}>
              <QESAlert classNames="mb-3" type="danger" persist={true}>
                <strong>Warning!</strong> You can fail your project manually if you believe it will not finish.
                <QESButton
                  qesTestId="access-reports-dashboard__overview--fail-job"
                  className="ms-3"
                  onClick={failProject}
                >
                  Fail Project
                </QESButton>
              </QESAlert>
            </RoleRestricted>
          </React.Fragment>
        );
        break;
      case AccessReportStatusEnum.Succeeded:
        if (!accessReport.dateArchived) {
          result = (
            <React.Fragment>
              <RoleRestricted allowedRoles={[Roles.ACCESS_REPORTS_WIZARD_ADMIN]}>
                <QESAlert classNames="mb-3" type="info" persist={true}>
                  Your project can be archived.

                  <QESButton
                    qesTestId="access-reports-dashboard__overview--archive"
                    className="ms-3"
                    onClick={archiveProject}
                  >
                    <QesIcon iconName={QesIcons.Archive} description="Archive" className="mb2" /> Archive Project
                  </QESButton>
                </QESAlert>
              </RoleRestricted>
            </React.Fragment>
          );
        }
        else {
          result = (
            <RoleRestricted allowedRoles={[Roles.ACCESS_REPORTS_WIZARD_ADMIN]}>
              <QESAlert classNames="mb-3" type="info" persist={true}>
                Your project can be unarchived.

                <QESButton
                  qesTestId="access-reports-dashboard__overview--unarchive"
                  className="ms-3"
                  onClick={unarchiveProject}
                >
                  <QesIcon iconName={QesIcons.Archive} description="Unarchive" className="mb2" /> Unarchive Project
                </QESButton>
              </QESAlert>
            </RoleRestricted>
          );
        }

        break;
      case AccessReportStatusEnum.Unknown:
        break;
      default:
        break;
    }

    const newStatusBanner: JSX.Element = (
      <div className="row">
        <div className="col-12">
          {result}
        </div>
      </div>
    );

    setStatusBanner(newStatusBanner);
  }

  const handleNotifications = async (message: Message): Promise<void> => {
    switch (message?.notificationType) {
      case MessageType.AccessReportsRunStatusChange:
        const projectRunStatusChangePayload: AccessReportRunStatusChangeNotificationMessagePayload = message.payload as AccessReportRunStatusChangeNotificationMessagePayload;

        if (projectRunStatusChangePayload?.accessReportId === accessReport?.accessReportId && accessReport.status !== projectRunStatusChangePayload.status) {
          setAccessReport((prevAccessReport) => ({
            ...prevAccessReport,
            status: projectRunStatusChangePayload.status,
          }));
        }
        break;
      default:
        break;
    }
  }

  const renderResults = (): React.ReactNode => {
    return (
      <div className="row">
        <div className="col-12">
          <legend className="mb0">Files:</legend>
        </div>

        <div className="col-12">
          <div className="mb-1">
            <TooltipWrapper containerClassName="ms-3" text="Download" triggerWidth="100%">
              <QESButton
                qesTestId="access-reports-dashboard__overview--download"
                qesType="secondary"
                onClick={(e) => { preventDefaultMouseEvent(e); handleFileLinkClick(accessReport, true, "PDF"); }}
                className="demibold p-0"
              >
                {`${accessReport.reportTitle}.pdf`}
              </QESButton>
            </TooltipWrapper>
            <span className="ms-3">|</span>
            <TooltipWrapper containerClassName="ms-1" text="Open in new tab" triggerWidth="100%">
              <QESButton
                className="px-2 py-0"
                qesType="secondary"
                qesTestId="access-reports-dashboard__overview--open-pdf"
                onClick={() => handleFileLinkClick(accessReport, false, "PDF")}
                aria-label="Open in a new tab"
              >
                <QesIcon iconName={QesIcons.ShareV4} description="Open" className="mb2" />
              </QESButton>
            </TooltipWrapper>
          </div>
        </div>

        <div className="col-12">
          <div className="mb-1">
            <TooltipWrapper containerClassName="ms-3" text="Download" triggerWidth="100%">
              <QESButton
                qesTestId="access-reports-dashboard__overview--xlsx"
                qesType="secondary"
                onClick={(e) => { preventDefaultMouseEvent(e); handleFileLinkClick(accessReport, true, "XLSX"); }}
                className="demibold p-0"
              >
                {`${accessReport.reportTitle}.xlsx`}
              </QESButton>
            </TooltipWrapper>
          </div>
        </div>

        {user.roles.includes(Roles.ACCESS_REPORTS_WIZARD_ADMIN) &&
          <div className="col-12">
            <div className="mb-3">
              <TooltipWrapper containerClassName="ms-3" text="Download" triggerWidth="100%">
                <QESButton
                  qesTestId="access-reports-dashboard__overview--qpx"
                  qesType="secondary"
                  onClick={(e) => { preventDefaultMouseEvent(e); handleFileLinkClick(accessReport, true, "QPX"); }}
                  className="demibold p-0"
                >
                  {`${accessReport.reportTitle}.qpx`}
                </QESButton>
              </TooltipWrapper>
            </div>
          </div>
        }
      </div>
    );
  }

  const renderSummary = (): React.ReactNode => {
    const accessReportParameters: AccessReportParametersModel = accessReport.parameters;

    // the following properties do not get preserved in the back end (but will ALWAYS be true, as we do not submit unchecked nodes),
    // and must be turned back on to display to the ui
    for (const summaryPage of accessReportParameters.accessReportsWizardSummaryPages) {
      summaryPage.accessSummary = true;
    }

    for (const detailsPage of accessReportParameters.accessReportsWizardDetailsPages) {
      detailsPage.accessDetail = true;
    }

    for (const employeeCountPage of accessReportParameters.employeeCountPages) {
      employeeCountPage.employeeCount = true;
    }

    for (const accessOverviewPage of accessReportParameters.accessOverviewPages) {
      accessOverviewPage.accessOverview = true;
    }

    for (const accessListingPage of accessReportParameters.accessListingPages) {
      accessListingPage.accessListing = true;
    }

    for (const accessRadiusSearchPage of accessReportParameters.accessRadiusSearchPages) {
      accessRadiusSearchPage.accessRadiusSearch = true;
    }

    for (const providerCountPage of accessReportParameters.providerCountPages) {
      providerCountPage.providerCount = true;
    }

    for (const accessCriteria of accessReportParameters.product.accessCriterion) {
      accessCriteria.selected = true;
    }

    return (
      <AccessReportSummary
        accessReportParameters={accessReportParameters}
      />
    );
  }

  const runProject = async (): Promise<void> => {
    const newAccessReport = {
      ...accessReport,
      status: AccessReportStatusEnum.Running,
    };
    setAccessReport(newAccessReport);
    await accessReportsService.createAccessReportJob(newAccessReport.accessReportId);
  }

  const archiveProject = async (): Promise<void> => {
    const archive: ArchiveAccessReportsBulkModel = {
      accessReportIds: [accessReport.accessReportId],
    };

    const archived: boolean = await accessReportsService.archiveAccessReport(archive);

    if (archived) {
      setAccessReport((prevAccessReport) => ({
        ...prevAccessReport,
        dateArchived: new Date(),
      }));
    }
  }

  const unarchiveProject = async (): Promise<void> => {
    const unarchive: ArchiveAccessReportsBulkModel = {
      accessReportIds: [accessReport.accessReportId],
    };

    const unarchived: boolean = await accessReportsService.unarchiveAccessReport(unarchive);

    if (unarchived) {
      setAccessReport((prevAccessReport) => ({
        ...prevAccessReport,
        dateArchived: null,
      }));
    }
  }

  const failProject = async (): Promise<void> => {
    const newAccessReport = {
      ...accessReport,
      status: AccessReportStatusEnum.Failed,
    };
    setAccessReport(newAccessReport);
    await accessReportsService.failAccessReport(newAccessReport.qesJobId, newAccessReport.accessReportId, newAccessReport.userId)
  };

  return loading ?
    <LoadingSpinner /> :
    !accessReport ?
      <NotFound /> : (
        <Page className="bg-white" documentTitle={`${accessReport?.reportTitle || "Loading"} - Access Reports`}>
          <Breadcrumbs
            items={[
              {
                link: "/access-reports",
                text: "Access Reports",
              },
              {
                text: accessReport.reportTitle,
              },
            ]}
          />
          <PageInner>
            <PageSection>
              <div className="row">
                <div className="col-12">
                  <h3 className="mt-3 mb-3 header-l demibold">
                    {accessReport.reportTitle}

                    <hr />
                  </h3>
                </div>
              </div>

              {statusBanner}

              {(accessReport.status === AccessReportStatusEnum.Succeeded && !loading) &&
                renderResults()
              }

              {renderSummary()}
            </PageSection>
          </PageInner>
        </Page>
      );
};
